// ============================================================
// Parameter cell references on the Parameters sheet
// ============================================================
export const PARAM_CELLS = {
    companyName: 'Parameters!$B$1',
    companyId: 'Parameters!$C$1',
    company: 'Parameters!$C$1',
    period: 'Parameters!$B$2',
    connection: 'Parameters!$B$3',
    periodStart: 'Parameters!$B$7',
    periodEnd: 'Parameters!$B$8',
    fiscalYearStart: 'Parameters!$B$9',
    fiscalYearEnd: 'Parameters!$B$10',
};
// ============================================================
// Core formula builder
// ============================================================
/**
 * Returns a formula string (without leading =) or null for label columns.
 */
export function buildCellFormula(ctx) {
    const { columnType, accountCode, excelRow, columnLetterMap, userFormula, periodOffset, budgetName, baseColumnLetter, compareColumnLetter, } = ctx;
    switch (columnType) {
        case 'label':
        case 'account_code':
            return null;
        case 'current':
            return `CLM.GL_BALANCE("${accountCode}",${PARAM_CELLS.periodEnd},${PARAM_CELLS.company},${PARAM_CELLS.connection})`;
        case 'prior_period':
            return `CLM.GL_BALANCE("${accountCode}",EOMONTH(${PARAM_CELLS.periodEnd},-1),${PARAM_CELLS.company},${PARAM_CELLS.connection})`;
        case 'prior_year':
            return `CLM.GL_BALANCE("${accountCode}",DATE(YEAR(${PARAM_CELLS.periodEnd})-1,MONTH(${PARAM_CELLS.periodEnd}),DAY(${PARAM_CELLS.periodEnd})),${PARAM_CELLS.company},${PARAM_CELLS.connection})`;
        case 'budget':
            return `CLM.BUDGET_AMOUNT("${budgetName ?? ''}","${accountCode}",${PARAM_CELLS.periodStart},${PARAM_CELLS.periodEnd},${PARAM_CELLS.company},${PARAM_CELLS.connection})`;
        case 'ytd':
            return `CLM.GL_SUM("${accountCode}",${PARAM_CELLS.fiscalYearStart},${PARAM_CELLS.periodEnd},${PARAM_CELLS.company},${PARAM_CELLS.connection})`;
        case 'variance_amount': {
            const baseCol = baseColumnLetter ?? columnLetterMap.get('current');
            const compCol = compareColumnLetter ?? columnLetterMap.get('budget') ?? columnLetterMap.get('prior_period');
            if (!baseCol || !compCol)
                return '0';
            return `${baseCol}${excelRow}-${compCol}${excelRow}`;
        }
        case 'variance_percent': {
            const baseCol = baseColumnLetter ?? columnLetterMap.get('current');
            const compCol = compareColumnLetter ?? columnLetterMap.get('budget') ?? columnLetterMap.get('prior_period');
            if (!baseCol || !compCol)
                return '0';
            return `IF(${compCol}${excelRow}=0,0,(${baseCol}${excelRow}-${compCol}${excelRow})/ABS(${compCol}${excelRow}))`;
        }
        case 'period_offset': {
            const offset = periodOffset ?? 0;
            return `CLM.GL_BALANCE("${accountCode}",EOMONTH(${PARAM_CELLS.periodEnd},${-Math.abs(offset)}),${PARAM_CELLS.company},${PARAM_CELLS.connection})`;
        }
        case 'year_offset': {
            const offset = periodOffset ?? 0;
            return `CLM.GL_BALANCE("${accountCode}",DATE(YEAR(${PARAM_CELLS.periodEnd})-${Math.abs(offset)},MONTH(${PARAM_CELLS.periodEnd}),DAY(${PARAM_CELLS.periodEnd})),${PARAM_CELLS.company},${PARAM_CELLS.connection})`;
        }
        case 'calculated':
            return userFormula ?? null;
    }
}
// ============================================================
// SUM formula builder
// ============================================================
/**
 * Builds a SUM formula optimizing contiguous ranges.
 * e.g., [3,4,5,8,9] -> "SUM(C3:C5,C8:C9)"
 */
export function buildSumFormula(columnLetter, dataRowNumbers) {
    if (dataRowNumbers.length === 0)
        return '0';
    const sorted = [...dataRowNumbers].sort((a, b) => a - b);
    const ranges = [];
    let rangeStart = sorted[0];
    let rangeEnd = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        if (sorted[i] === rangeEnd + 1) {
            rangeEnd = sorted[i];
        }
        else {
            ranges.push(rangeStart === rangeEnd
                ? `${columnLetter}${rangeStart}`
                : `${columnLetter}${rangeStart}:${columnLetter}${rangeEnd}`);
            rangeStart = sorted[i];
            rangeEnd = sorted[i];
        }
    }
    ranges.push(rangeStart === rangeEnd
        ? `${columnLetter}${rangeStart}`
        : `${columnLetter}${rangeStart}:${columnLetter}${rangeEnd}`);
    return `SUM(${ranges.join(',')})`;
}
// ============================================================
// Column index to letter
// ============================================================
/**
 * Converts 0-based column index to Excel column letter.
 * 0->A, 25->Z, 26->AA, 27->AB
 */
export function columnIndexToLetter(index) {
    let result = '';
    let n = index;
    while (n >= 0) {
        result = String.fromCharCode((n % 26) + 65) + result;
        n = Math.floor(n / 26) - 1;
    }
    return result;
}
// ============================================================
// Number format strings
// ============================================================
/**
 * Returns an Excel numFmt string based on column format and theme negative format.
 */
export function getNumberFormat(format, themeNegativeFormat) {
    if (!format)
        return '#,##0.00';
    const decimals = format.decimals ?? 2;
    const negFmt = format.negativeFormat ?? (themeNegativeFormat === 'red' ? 'minus' : 'parentheses');
    const decimalPart = decimals > 0 ? '.' + '0'.repeat(decimals) : '';
    switch (format.type) {
        case 'currency': {
            const pos = `_($* #,##0${decimalPart}_)`;
            if (negFmt === 'parentheses' || themeNegativeFormat === 'both') {
                const neg = themeNegativeFormat === 'both' || themeNegativeFormat === 'red'
                    ? `[Red]_($* (#,##0${decimalPart})`
                    : `_($* (#,##0${decimalPart})`;
                return `${pos};${neg};_($* "-"??_)`;
            }
            if (negFmt === 'red') {
                return `${pos};[Red]-$#,##0${decimalPart};_($* "-"??_)`;
            }
            // minus (default)
            return `$#,##0${decimalPart};-$#,##0${decimalPart}`;
        }
        case 'percent':
            return `0${decimalPart}%`;
        case 'number': {
            const numPos = `#,##0${decimalPart}`;
            if (negFmt === 'parentheses') {
                return `${numPos}_);(${numPos})`;
            }
            if (negFmt === 'red') {
                return `${numPos};[Red]-${numPos}`;
            }
            return numPos;
        }
        case 'text':
            return '@';
    }
}
// ============================================================
// Color conversion
// ============================================================
/**
 * Converts CSS hex color (#1e3a5f or #fff) to ExcelJS ARGB (FF1e3a5f).
 */
export function hexToArgb(hex) {
    const clean = hex.replace('#', '');
    if (clean.length === 3) {
        const expanded = clean[0] + clean[0] + clean[1] + clean[1] + clean[2] + clean[2];
        return 'FF' + expanded;
    }
    return 'FF' + clean;
}
//# sourceMappingURL=formula-builder.js.map